/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { AddCircleOutline, Search } from '@material-ui/icons';

import { useFormik } from 'formik';
import { useQuery } from 'hooks/useQuery';
import { xpcellService } from 'services';

import { ComponentsTable } from './components/ComponentsTable';
import { CreateProductDialog } from './components/CreateProductDialog';
import { UploadFile } from './components/UpdateByFile';
import { initialValues } from './formik/initialValues';
import { validationSchema } from './formik/validationSchema';
import { GradientButton, useStyles } from './styles';

export function ComponentSellPrice() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [processFilter, setProcessFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [file, setFile] = useState(null);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [process] = useQuery(() => xpcellService.getProcess(), []);
  const [components, , loadingComponents, refetchComponents] = useQuery(
    () =>
      xpcellService.listComponentSellPrice({
        skip: page * rowsPerPage,
        take: rowsPerPage,
        search: searchTerm,
        process: processFilter !== 'all' ? processFilter : undefined,
      }),
    [page, rowsPerPage, searchTerm, processFilter],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 700);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const processOptions = (process || []).map((option) => ({
    label: option.descricao,
    value: option.idCadastro,
  }));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleProcessFilterChange = (event) => {
    setProcessFilter(event.target.value);
    setPage(0);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      if (editingProduct) {
        try {
          await xpcellService.updateComponentSellProcess(values);
          refetchComponents();
          toast.success('Componente atualizado com sucesso');
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Erro ao atualizar o valor do componente');
        }
        setProducts(products.map((p) => (p.id === editingProduct.id ? { ...p, ...values } : p)));
        setEditingProduct(null);
      } else {
        try {
          await xpcellService.createComponentSellPrice(values);
          toast.success('Componente criado com sucesso');
          refetchComponents();
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Erro ao criar o componente');
          return;
        }
      }
      formik.resetForm();
      setOpenDialog(false);
      setIsLoading(false);
    },
  });

  const handleEdit = (product) => {
    setEditingProduct(product);

    const processId = processOptions.find((item) => item.label === product?.process);

    formik.setValues({
      id: product.id,
      part_number: product.part_number,
      sell_price: product.sell_price,
      cost_price: product.cost_price,
      process: [processId?.value],
      active: 1,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (product) => {
    try {
      await xpcellService.updateComponentSellProcess({ ...product, active: 0 });
      refetchComponents();
      toast.success('Componente deletado com sucesso');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao deletar o valor do componente');
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleProcessChange = (event) => {
    setSelectedProcesses(event.target.value);
  };

  const handleResetFileImport = () => {
    setSelectedProcesses([]);
    refetchComponents();
    setFile(null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant='h4' style={{ fontWeight: 700 }}>
          Gerenciamento de Preços
        </Typography>
        <GradientButton
          variant='contained'
          startIcon={<AddCircleOutline />}
          onClick={() => setOpenDialog(true)}
        >
          Novo Produto
        </GradientButton>
      </div>

      <UploadFile
        selectedProcesses={selectedProcesses}
        processOptions={processOptions}
        file={file}
        isLoading={isLoading}
        handleFileChange={handleFileChange}
        handleProcessChange={handleProcessChange}
        handleResetFileImport={handleResetFileImport}
      />

      <Card className={classes.card}>
        <div className={classes.cardHeader}>
          <Typography variant='h6' style={{ fontWeight: 600 }}>
            Lista de Produtos
          </Typography>
        </div>
        <CardContent className={classes.cardContent}>
          <div className={classes.searchContainer}>
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              placeholder='Pesquisar por Part Number'
              value={searchInput}
              onChange={handleSearchInputChange}
              InputProps={{
                startAdornment: <Search color='action' style={{ marginRight: 8 }} />,
              }}
            />
            <FormControl variant='outlined' size='small' style={{ minWidth: 200 }}>
              <InputLabel>Filtrar por Processo</InputLabel>
              <Select
                value={processFilter}
                onChange={handleProcessFilterChange}
                label='Filtrar por Processo'
              >
                <MenuItem value='all'>Todos os Processos</MenuItem>
                {processOptions.map((process) => (
                  <MenuItem key={process.value} value={process.value}>
                    {process.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <ComponentsTable
            components={components?.results || []}
            loading={loadingComponents}
            totalCount={components?.totalCount || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
          />
        </CardContent>
      </Card>

      <CreateProductDialog
        isOpen={openDialog}
        handleClose={handleCloseDialog}
        editingProduct={editingProduct}
        formik={formik}
        processOptions={processOptions}
        isLoading={isLoading}
      />
    </div>
  );
}
